import api from "../api";

export type SecureFileStatus = "new" | "downloaded";
export type SecureFileDisplayStatus =
  | "new"
  | "downloaded"
  | "sent"
  | "received"
  | "uploaded";

export type SecureFileDeleteReason = "" | "manual" | "expired";

export interface SecureFile {
  id: number;
  file_name: string;
  content_type: string;
  size: number;
  message: string;
  status: SecureFileStatus;
  status_display: string;
  display_status: SecureFileDisplayStatus;
  is_silent: boolean;
  is_archived: boolean;
  archived_at: string | null;
  deletes_on: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  delete_reason: SecureFileDeleteReason;
  delete_reason_display: string;
  deleted_by_name: string;
  purges_on: string | null;
  can_restore: boolean;
  created_at: string;
  downloaded_at: string | null;
  client_id: number | null;
  client_name: string | null;
  uploaded_by_id: number | null;
  uploaded_by_name: string;
  uploaded_for_id: number | null;
  uploaded_for_name: string;
  can_delete: boolean;
}

export interface SecureFileListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: SecureFile[];
}

export type SecureFileDirection = "received" | "sent";

export type SecureFileTab = "shared" | "archived" | "deleted";

export interface SecureFileListRequest {
  tab?: SecureFileTab;
  search?: string;
  client?: number | null;
  display_status?: SecureFileDisplayStatus | null;
  direction?: SecureFileDirection | null;
  sort_by?: string | null;
  sort_order?: "asc" | "desc" | null;
  page?: number;
  page_size?: number;
}

export interface SecureRecipient {
  id: number;
  name: string;
  email: string;
  has_email: boolean;
}

export interface SecureClientOption {
  id: number;
  company_name: string;
  company_code: string;
}

export interface PresignResponse {
  upload_url: string;
  s3_key: string;
  s3_bucket: string;
  file_name: string;
  headers: Record<string, string>;
}

export interface FinalizeRequest {
  s3_key: string;
  recipient_id: number;
  client_id?: number | null;
  file_name: string;
  content_type: string;
  message?: string;
  is_silent?: boolean;
}

const BASE = "/secure-uploads";

export const secureUploadsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSecureFiles: builder.query<SecureFileListResponse, SecureFileListRequest>({
      query: ({
        tab,
        search,
        client,
        display_status,
        direction,
        sort_by,
        sort_order,
        page,
        page_size,
      }) => ({
        url: `${BASE}/`,
        params: {
          tab: tab || "shared",
          search: search || undefined,
          client: client || undefined,
          display_status: display_status || undefined,
          direction: direction || undefined,
          sort_by: sort_by || undefined,
          sort_order: sort_by ? sort_order || "asc" : undefined,
          page: page ?? 1,
          page_size: page_size ?? 25,
        },
      }),
      providesTags: ["SecureUploads"],
    }),

    getSecureRecipients: builder.query<
      SecureRecipient[],
      { client_id?: number | null }
    >({
      query: ({ client_id }) => ({
        url: `${BASE}/recipients/`,
        params: client_id ? { client_id } : {},
      }),
    }),

    getSecureClients: builder.query<SecureClientOption[], void>({
      query: () => ({ url: `${BASE}/clients/` }),
    }),

    getSecureUnreadCount: builder.query<{ unread: number }, void>({
      query: () => ({ url: `${BASE}/unread-count/` }),
      providesTags: ["SecureUploadsUnread"],
    }),

    markSecureFilesSeen: builder.mutation<{ marked: number }, void>({
      query: () => ({
        url: `${BASE}/mark-seen/`,
        method: "POST",
      }),
      invalidatesTags: ["SecureUploadsUnread"],
    }),

    presignSecureUpload: builder.mutation<
      PresignResponse,
      { file_name: string; content_type: string }
    >({
      query: (body) => ({
        url: `${BASE}/upload/presign/`,
        method: "POST",
        body,
      }),
    }),

    finalizeSecureUpload: builder.mutation<
      SecureFile & { email_sent: boolean },
      FinalizeRequest
    >({
      query: (body) => ({
        url: `${BASE}/upload/finalize/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread"],
    }),

    getSecureFileAccessUrl: builder.mutation<
      { url: string; file_name: string },
      { id: number; mode?: "preview" }
    >({
      query: ({ id, mode }) => ({
        url: `${BASE}/${id}/access/`,
        method: "POST",
        body: mode ? { mode } : {},
      }),
      // Preview is read-only server-side — nothing to refetch.
      invalidatesTags: (_result, _error, arg) =>
        arg.mode === "preview" ? [] : ["SecureUploads", "SecureUploadsUnread"],
    }),

    archiveSecureFile: builder.mutation<SecureFile, number>({
      query: (id) => ({
        url: `${BASE}/${id}/archive/`,
        method: "POST",
      }),
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread"],
    }),

    unarchiveSecureFile: builder.mutation<SecureFile, number>({
      query: (id) => ({
        url: `${BASE}/${id}/unarchive/`,
        method: "POST",
      }),
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread"],
    }),

    deleteSecureFile: builder.mutation<void, number>({
      query: (id) => ({
        url: `${BASE}/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread"],
    }),

    restoreSecureFile: builder.mutation<SecureFile, number>({
      query: (id) => ({
        url: `${BASE}/${id}/restore/`,
        method: "POST",
      }),
      invalidatesTags: ["SecureUploads", "SecureUploadsUnread"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSecureFilesQuery,
  useGetSecureRecipientsQuery,
  useGetSecureClientsQuery,
  useGetSecureUnreadCountQuery,
  useMarkSecureFilesSeenMutation,
  usePresignSecureUploadMutation,
  useFinalizeSecureUploadMutation,
  useGetSecureFileAccessUrlMutation,
  useArchiveSecureFileMutation,
  useUnarchiveSecureFileMutation,
  useDeleteSecureFileMutation,
  useRestoreSecureFileMutation,
} = secureUploadsApi;
